import type { Place, PlaceLevel, SearchPostingsQuery } from "./api/catalogue";
import { cutAt } from "./letters";
import { countryName } from "./openings";

export interface JobLocation {
    level: PlaceLevel | "ANY" | "TEXT";
    value: string;
    name: string;
    country: string | null;
    region: string | null;
}

export const ANYWHERE: JobLocation = {
    level: "ANY",
    value: "",
    name: "Anywhere",
    country: null,
    region: null,
};

/** A catalogue city keeps its state and country, so identically named cities stay distinct. */
export function locationLabel(place: JobLocation | Place): string {
    if (place.level === "ANY") return "Anywhere";
    if (place.level === "TEXT") return place.name;
    const parts = [place.name];
    if (place.level === "CITY" && place.region) {
        const code = place.region.split("-").slice(1).join("-");
        if (code && !place.name.toLowerCase().endsWith(`, ${code.toLowerCase()}`)) parts.push(code);
    }
    if (place.country && place.level !== "COUNTRY") parts.push(countryName(place.country));
    return parts.join(", ");
}

/**
 * A name from the address, as the page may show it: no control
 * characters, no direction overrides that would draw it backwards, and
 * at most 200 units, cut at a whole letter.
 */
const clean = (value: string | null) =>
    cutAt(
        // eslint-disable-next-line no-control-regex
        (value ?? "").replace(/[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, "").trim(),
        200,
    );
const countryCode = (value: string | null) =>
    /^[a-z]{2}$/i.test(value ?? "") ? value!.toUpperCase() : null;
const regionCode = (value: string | null) =>
    /^[a-z]{2}-[a-z0-9]{1,4}$/i.test(value ?? "") ? value!.toUpperCase() : null;

/** No override is distinct from Anywhere, which deliberately opts out of nearby defaults. */
export function readJobLocation(params: URLSearchParams): JobLocation | null {
    const level = params.get("where");
    if (level === "ANY") return ANYWHERE;
    if (!["CITY", "REGION", "COUNTRY", "TEXT"].includes(level ?? "")) return null;
    let value = clean(params.get("place"));
    if (!value) return null;
    let country = countryCode(params.get("country"));
    let region = regionCode(params.get("region"));
    if (level === "COUNTRY" && !countryCode(value)) return null;
    if (level === "REGION" && !regionCode(value)) return null;
    if (level === "COUNTRY") {
        value = countryCode(value)!;
        country = value;
        region = null;
    }
    if (level === "REGION") {
        value = regionCode(value)!;
        country = value.split("-")[0];
        region = value;
    }
    if (level === "CITY" && region) {
        const parent = region.split("-")[0];
        if (country && country !== parent) return null;
        country = parent;
    }
    return {
        level: level as JobLocation["level"],
        value,
        name: clean(params.get("place_name")) || value,
        country,
        region,
    };
}

export function writeJobLocation(
    params: URLSearchParams,
    place: JobLocation | null,
): URLSearchParams {
    const next = new URLSearchParams(params);
    for (const key of [
        "where",
        "place",
        "place_name",
        "country",
        "region",
        "city",
        "posting",
        "next_cursor",
    ])
        next.delete(key);
    if (!place) return next;
    next.set("where", place.level);
    if (place.level !== "ANY") {
        next.set("place", place.value);
        next.set("place_name", place.name);
        if (place.country) next.set("country", place.country);
        if (place.region) next.set("region", place.region);
    }
    return next;
}

/**
 * Explicit searches replace every nearby or envoy location constraint. A
 * place asks for itself alone: a posting in London and Paris states no one
 * country, so a city asked for with its country would lose it.
 */
export function jobLocationQuery(place: JobLocation | null): SearchPostingsQuery {
    if (!place) return {};
    return {
        country: place.level === "COUNTRY" ? [place.value] : undefined,
        region: place.level === "REGION" ? [place.value] : undefined,
        city: place.level === "CITY" ? [place.value] : undefined,
        location: place.level === "TEXT" ? place.value : undefined,
        remote_country_wide: false,
        unstated: false,
    };
}

export function matchingLocations(
    places: readonly Place[],
    typed: string,
    regions: readonly Place[] = [],
): Place[] {
    const tails = typed
        .toLowerCase()
        .split(",")
        .slice(1)
        .map((part) => part.trim())
        .filter(Boolean);
    return places.filter((place) => {
        const regionName = regions.find((region) => region.value === place.region)?.name ?? "";
        const label =
            `${locationLabel(place)} ${place.country ?? ""} ${place.region ?? ""} ${regionName}`.toLowerCase();
        return tails.every((part) => label.includes(part));
    });
}
